import { useMutation } from '@apollo/client';
import { Formik, FormikProps } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { CurrentUserContext } from '../../../../contexts/currentUserContext';
import {
  UserProfessionContext
  // UserProfessionContextProvider
} from '../../../../contexts/userProfessionContext';
// import { GET_CURRENT_USER } from '../../../../graphql/GET_CURRENT_USER';
import { UPDATE_USER_PROFILE } from '../../../../graphql/UPDATE_USER_PROFILE';
import { GET_CURRENT_USER_me } from '../../../../graphql/types/GET_CURRENT_USER';
import UserAvatar from '../UserAvatar';
import UserSkillForm from '../UserSkillForm';
import UserCollaborateWith from '../UserCollaborateWith';
import countries from '../../../../assets/countries.json';

// Import step components
import NameStep from './steps/NameStep';
import ProfessionStep from './steps/ProfessionStep';
import SkillsStep from './steps/SkillsStep';
import BioStep from './steps/BioStep';
import CollaborationStep from './steps/CollaborationStep';
import WebsiteStep from './steps/WebsiteStep';
import LocationStep from './steps/LocationStep';
import PhotoStep from './steps/PhotoStep';
import InterestsStep from './steps/InterestsStep';

// Import shared components
import ProgressBar from './ProgressBar';
import MascotCharacter from './MascotCharacter';

// Import styles
import './onboarding.sass';

/**
 * Define the steps of the onboarding form
 * Each step represents a different part of the user profile setup
 */
const STEPS = {
  NAME: 0,
  PROFESSION: 1,
  SKILLS: 2,
  BIO: 3,
  COLLABORATION: 4,
  WEBSITE: 5,
  LOCATION: 6,
  PHOTO: 7,
  INTERESTS: 8
};

const getBadgeText = (step: number) => {
  switch (step) {
    case STEPS.NAME:
      return 'About You';
    case STEPS.PROFESSION:
    case STEPS.LOCATION:
      return '';
    case STEPS.SKILLS:
    case STEPS.BIO:
    case STEPS.COLLABORATION:
    case STEPS.WEBSITE:
    case STEPS.PHOTO:
    case STEPS.INTERESTS:
      return 'Optional';
    default:
      return '';
  }
};

const getBadgeClass = (step: number) => {
  return step === STEPS.SKILLS ||
    step === STEPS.BIO ||
    step === STEPS.COLLABORATION ||
    step === STEPS.WEBSITE ||
    step === STEPS.PHOTO ||
    step === STEPS.INTERESTS
    ? 'badge-optional'
    : '';
};

/**
 * OnboardingForm Component
 *
 * Multi-step onboarding form that guides users through setting up their profile
 * Includes steps for personal info, professional details, and preferences
 */
const OnboardingForm: React.FunctionComponent = () => {
  // State to track the current step in the onboarding process
  const [currentStep, setCurrentStep] = useState(STEPS.NAME);

  // GraphQL mutation hook for updating user profile
  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    onCompleted: (data) => {
      console.log('Profile update completed successfully:', data);
      toast.success('User profile successfully updated.');
    },
    onError: (error) => {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile. Please try again.');
    }
  });

  // Get current user data from context
  const user: GET_CURRENT_USER_me = useContext(CurrentUserContext);

  /**
   * Navigate to the next step in the onboarding process
   */
  const goToNextStep = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  /**
   * Render the appropriate step content based on currentStep
   *
   * @param formikProps - Formik form state and handlers
   * @returns JSX element for the current step
   */
  const renderStepContent = (formikProps: FormikProps<any>) => {
    const {
      handleSubmit,
      isSubmitting,
      values,
      // handleChange,
      // setFieldValue,
      errors,
      setFieldTouched
    } = formikProps;

    /**
     * Handle next button click with optional field validation
     *
     * @param fieldToValidate - Optional field name to validate before proceeding
     */
    const handleNext = async (fieldToValidate?: string | string[]) => {
      if (fieldToValidate) {
        const fields = Array.isArray(fieldToValidate) ? fieldToValidate : [fieldToValidate];

        let hasError = false;

        for (const field of fields) {
          setFieldTouched(field, true);
        }

        const currentErrors = await formikProps.validateForm();

        for (const field of fields) {
          if (currentErrors[field]) {
            hasError = true;
          }
        }

        // Check if the fields are valid before proceeding
        if (!hasError) {
          goToNextStep();
        }
      } else {
        // If no field validation is needed, just go to next step
        goToNextStep();
      }
    };

    /**
     * Handle form submission for the final step
     */
    const handleFormSubmit = () => {
      console.log('Submitting onboarding form with values:', values);
      handleSubmit();
    };

    // Render the appropriate step component based on current step
    switch (currentStep) {
      case STEPS.NAME:
        return <NameStep formik={formikProps} goToNextStep={handleNext} />;

      case STEPS.PROFESSION:
        return (
          <ProfessionStep
            formik={formikProps}
            goToNextStep={handleNext}
            professions={user.professions ?? []}
            errors={errors}
            OnBoardingUserProfession={OnBoardingUserProfession}
          />
        );

      case STEPS.SKILLS:
        return (
          <SkillsStep
            goToNextStep={handleNext}
            skipStep={goToNextStep}
            UserSkillForm={UserSkillForm}
            skills={user.skills ?? []}
            hideTitle={true}
          />
        );

      case STEPS.BIO:
        return (
          <BioStep
            formik={formikProps}
            goToNextStep={handleNext}
            skipStep={goToNextStep}
          />
        );

        case STEPS.COLLABORATION:
          return (
            <CollaborationStep
              goToNextStep={handleNext}
              skipStep={goToNextStep}
              UserCollaborateWith={UserCollaborateWith}
            />
          );

      case STEPS.WEBSITE:
        return (
          <WebsiteStep
            formik={formikProps}
            goToNextStep={handleNext}
            skipStep={goToNextStep}
          />
        );

      case STEPS.LOCATION:
        return (
          <LocationStep
            formik={formikProps}
            goToNextStep={handleNext}
            countries={countries}
          />
        );

      case STEPS.PHOTO:
        return (
          <PhotoStep
            formik={formikProps}
            goToNextStep={handleNext}
            skipStep={goToNextStep}
            UserAvatar={UserAvatar}
            avatar={user?.avatar?.fileUrl ?? ''}
          />
        );

      case STEPS.INTERESTS:
        return (
          <InterestsStep
            formik={formikProps}
            skipStep={handleFormSubmit}
            onSubmit={handleFormSubmit}
            isSubmitting={isSubmitting}
          />
        );

      default:
        return null;
    }
  };

  // Calculate total number of steps for progress calculation
  const totalSteps = Object.keys(STEPS).length;
  const badgeText = getBadgeText(currentStep);
  const badgeClass = getBadgeClass(currentStep);

  return (
    <>
      {badgeText && (
        <div className={`badge-about-you badge-desktop ${badgeClass}`}>
          {badgeText}
        </div>
      )}

      <div className="onboarding-container">
        {badgeText && (
          <div className={`badge-about-you badge-mobile ${badgeClass}`}>
            {badgeText}
          </div>
        )}

        {/* Mascot Character - Position changes based on progress */}
        <div className="mascot-wrapper">
          <MascotCharacter currentStep={currentStep} totalSteps={totalSteps} />
        </div>

        {/* Form Container */}
        <div className="onboarding-form-container">
          {/* Progress Indicator */}
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />

          {/* Formik Form with all steps */}
          <Formik
            enableReinitialize={true}
            initialValues={{
              firstName: user?.profile?.name?.split(' ')[0] ?? '',
              lastName: user?.profile?.name?.split(' ')[1] ?? '',
              bio: user?.profile?.bio ?? '',
              collaborationStatus: user?.profile?.collaborationStatus ?? '',
              professions: [],
              country: user?.profile?.country ?? '',
              city: user?.profile?.city ?? '',
              website: user?.profile?.website ?? '',
              userInterests: user?.userInterests ?? [], // User interests array
              skills: [] // Initialize skills array
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                console.log(
                  'Submitting onboarding form with complete values:',
                  values
                );

                // Prepare mutation variables with all required fields
                const mutationVariables = {
                  firstName: values.firstName,
                  lastName: values.lastName,
                  name: `${values.firstName} ${values.lastName}`, // Combine first and last name
                  bio: values.bio,
                  website: values.website,
                  collaborationStatus: values.collaborationStatus,
                  country: values.country,
                  city: values.city,
                  userInterests: values.userInterests, // Include user interests
                  skills: values.skills || [], // Include skills array
                  professions: values.professions || [], // Include professions array
                  isOnboarded: true // Mark user as onboarded
                };

                console.log('Mutation variables:', mutationVariables);

                const result = await updateUserProfile({
                  variables: mutationVariables,
                  fetchPolicy: 'no-cache',
                  errorPolicy: 'all'
                });

                // Debug logging to understand the mutation response
                console.log('Mutation result:', result);
                console.log('result.data:', result.data);
                console.log(
                  'result.data?.editUserProfile:',
                  result.data?.editUserProfile
                );

                // Check if mutation was successful and redirect
                if (result.data?.editUserProfile) {
                  console.log(
                    'Onboarding successful, redirecting to dashboard...'
                  );
                  toast.success('Profile setup completed successfully!');
                  // Use a short delay to allow the toast to show
                  setTimeout(() => {
                    window.location.replace('/');
                  }, 1000);
                } else {
                  console.error('Mutation completed but no data returned');
                  toast.error('Something went wrong. Please try again.');
                }
              } catch (error) {
                console.error('Onboarding submission failed:', error);

                // More detailed error logging
                if (error.graphQLErrors) {
                  console.error('GraphQL errors:', error.graphQLErrors);
                }
                if (error.networkError) {
                  console.error('Network error:', error.networkError);
                }

                toast.error('Failed to update profile. Please try again.');
              } finally {
                setSubmitting(false);
              }
            }}
            validationSchema={Yup.object().shape({
              firstName: Yup.string().required('This field is required'),
              lastName: Yup.string().required('This field is required'),
              professions: Yup.array().min(
                1,
                'At least one profession is required'
              ),
              country: Yup.string().required('This field is required'),
              city: Yup.string().required('This field is required'),
              bio: Yup.string().notRequired(),
              userInterests: Yup.array() // Array validation for user interests (not required)
            })}
          >
            {(formikProps) => renderStepContent(formikProps)}
          </Formik>
        </div>
      </div>
    </>
  );
};

/**
 * OnBoardingUserProfession Component
 *
 * Helper component that syncs selected professions with Formik state
 * This ensures the professions selected in the UI are properly stored in the form
 *
 * @param props - Component props containing setFieldValue function
 */
function OnBoardingUserProfession(props: { setFieldValue: any }) {
  const { professionNames } = useContext(UserProfessionContext);

  useEffect(() => {
    // Update the professions field whenever profession names change
    props.setFieldValue('professions', professionNames);
  }, [professionNames, props.setFieldValue]);

  return null;
}

export default OnboardingForm;
